import React, { useEffect, useState } from 'react';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Footer from '../components/Footer';

function ForgotPassword() {
  useDocumentTitle('Forgot Password');
  useScripts();
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  useEffect(() => {
    // Add login class to body for proper styling
    document.body.classList.add('login');
    
    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove('login');
    };
  }, []);
  
  useEffect(() => {
    // Trigger animations when component mounts
    const triggerAnimations = () => {
      // Dispatch scroll event to trigger css3-animate-it library
      window.dispatchEvent(new Event('scroll'));
      
      // Also manually trigger animations for visible elements
      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.animated');
        animatedElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible) {
            const originalClasses = el.className;
            el.className = originalClasses.replace(/\banimated\b/g, '').trim();
            void el.offsetHeight; // Force reflow
            setTimeout(() => {
              el.className = originalClasses;
            }, 10);
          }
        });
      }, 100);
    };
    
    // Trigger immediately and after a short delay
    triggerAnimations();
    setTimeout(triggerAnimations, 200);
    setTimeout(triggerAnimations, 500);
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    
    try {
      // TODO: Replace with actual forgot password API endpoint
      const response = await fetch(
        'https://faas-nyc1-2ef2e6cc.doserverless.co/api/v1/web/fn-a14af637-c32b-4663-8e91-3fbcdf60921a/vpn/vpn-forgot-password',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
          }),
        }
      );
      
      // Parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        if (response.ok) {
          setSuccessMessage('If an account exists with this email, a password reset link has been sent.');
          setIsLoading(false);
          return;
        }
        setErrorMessage('An error occurred. Please try again.');
        setIsLoading(false);
        return;
      }
      
      // Check statusCode in response body
      const statusCode = data.statusCode || response.status;
      
      if (statusCode === 200 || response.ok) {
        setSuccessMessage('If an account exists with this email, a password reset link has been sent.');
        setEmail(''); // Clear email field on success
      } else if (statusCode === 404) {
        // Don't reveal if email exists or not for security
        setSuccessMessage('If an account exists with this email, a password reset link has been sent.');
        setEmail('');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      {/* Forgot Password Form */}
      <div id="forgot-password-form" className="form-contact animatedParent">
        <h3>Forgot Password</h3>
        <p style={{ textAlign: 'center', color: '#8198a6', marginBottom: '20px', fontSize: '14px' }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>
        <form className="animated growIn" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email address</label>
            <input 
              type="email" 
              required 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          {errorMessage && (
            <div className="form-error-message">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="form-success-message" style={{ 
              color: '#28a745', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {successMessage}
            </div>
          )}
          <div className="form-group text-center">
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
          <div className="form-group text-center" style={{ marginTop: '20px' }}>
            <p style={{ fontSize: '14px', color: '#8198a6', margin: 0 }}>
              Remember your password? <a href="/login" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>Back to login</a>
            </p>
          </div>
        </form>
      </div>
      
      <Footer />
    </>
  );
}

export default ForgotPassword;
